/**
 * @packageDocumentation
 * @module @yimoka/web-http/handle-axios-response
 * @summary 将 axios AxiosResponse / AxiosError 适配为 IHTTPResponse
 * @author keep
 * @since 0.0.1
 */

import {
  getCodeByStatus,
  IAny,
  IAnyObject,
  IHTTPCode,
  IHTTPResponse,
  unwrapSingleKeyData,
} from '@yimoka/shared';
import type { AxiosError, AxiosResponse } from 'axios';

/**
 * 把已解析的响应体归一化为 `{ code, msg, data }` 结构。
 *
 * - 如果 body 已经是 `{ code, msg|message|data, ... }` 标准信封 → 直接展开
 * - 否则把 body 作为 data，code 由 HTTP status 推导，msg 取 statusText
 *
 * @param data - 已解析的 body
 * @param status - HTTP 状态码
 * @param statusText - HTTP 状态文本
 * @returns 归一化字段
 */
export const getResponseData = (
  data: IAny,
  status: number,
  statusText: string,
): Record<string | number | symbol, IAny> => {
  if (
    data
    && typeof data === 'object'
    && typeof (data as IAnyObject).code !== 'undefined'
    && (
      typeof (data as IAnyObject).msg !== 'undefined'
      || typeof (data as IAnyObject).message !== 'undefined'
      || typeof (data as IAnyObject).data !== 'undefined'
    )
  ) {
    return { data: null, ...(data as IAnyObject) };
  }
  return {
    data,
    code: getCodeByStatus(status),
    msg: statusText,
  };
};

/**
 * 将 axios 的 {@link AxiosResponse} 转为 {@link IHTTPResponse}。
 *
 * @remarks
 * 出参 data 字段会通过 {@link unwrapSingleKeyData} 自动剥掉 gRPC 单 key 包装
 * （`{ value: T }` / `{ data: T[] }` → `T` / `T[]`）。
 *
 * @param response - axios 响应
 * @returns 统一响应结构
 */
export function handleAxiosResponse<T = IAny>(response: AxiosResponse): IHTTPResponse<T> {
  const { status, statusText, data, headers } = response;
  const resData = getResponseData(data, status, statusText);
  const res: IHTTPResponse<T> = {
    status,
    statusText,
    headers: headers as IHTTPResponse<T>['headers'],
    ...resData,
    data: unwrapSingleKeyData<T>(resData.data as T),
    // resData 由 getResponseData 保证非空，直接访问即可
    code: typeof resData.code === 'number' ? resData.code : getCodeByStatus(status),
    msg: (resData.msg ?? resData.message ?? statusText) as string,
  };

  return res;
}

/**
 * 构造网络错误响应（无 Response 对象时）。
 *
 * @param message - 错误信息
 * @returns IHTTPResponse
 */
export function createNetworkErrorResponse<T = IAny>(message?: string): IHTTPResponse<T> {
  return {
    code: IHTTPCode.networkError,
    msg: message ?? '网络出错',
    data: null as T,
    status: IHTTPCode.networkError,
    statusText: message ?? '网络出错',
  };
}

/**
 * 把 axios 抛出的错误归一化为 {@link IHTTPResponse}。
 *
 * - 4xx / 5xx 有 `error.response` → 走 {@link handleAxiosResponse}
 * - 取消 / 超时 / 网络层错误 → networkError
 *
 * @param error - axios catch 到的错误（保持 unknown 以兼容业务侧 try/catch 直接转入）
 * @returns 统一响应结构
 */
export function handleAxiosError<T = IAny>(error: unknown): IHTTPResponse<T> {
  if (error && typeof error === 'object') {
    const axiosErr = error as AxiosError;
    if (axiosErr.response) {
      return handleAxiosResponse<T>(axiosErr.response);
    }
    const message = typeof axiosErr.message === 'string' ? axiosErr.message : '网络出错';
    return createNetworkErrorResponse<T>(message);
  }
  return createNetworkErrorResponse<T>();
}
